// Surplus bundles on the laser packaging screen (2026-10-26): the to-do strip of bundles nobody has racked yet,
// and the actions on a surplus card. Calls + words: features/surplus/surplusBundles.js. The laser TV has a keyboard
// and a mouse, so "Labeled + racked" asks for the operator's PIN right here.
// Surplus crates (2026-11-03): while the API says crates are on (or the bundle already has a crate), the bundle's
// pieces go in a crate with a pallet beacon instead. Since 2026-11-04 (Mike: idiot-proof) the card shows numbered steps
// per case (`CrateSteps`: a new crate's beacon flashing on the beacon storage rack, the part's crate, no free beacon)
// and the buttons are "✓ Done: in crate BCN-031" (a tick, PIN), "Get a beacon", "Use existing crate…" (the
// CrateBeaconPrompt box) and "Flash BCN-031 again". Scanning the crate's beacon at the laser does the same.
import React, { createContext, useContext, useState } from "react";
import pinPrompt from "../utils/pinPrompt";
import crateBeaconPrompt from "./CrateBeaconPrompt";
import { KioskScanContext } from "../features/laserKiosk/kioskScan";
import {
  ConflictError, bundleLine, bundlePieces, crateDoneText, crateSteps, findCrate, labelStatusText, landedText,
  movePull, openCrate, previewPull, pullLine, putInCrate, rackBundle, reprintBundleLabel,
} from "../features/surplus/surplusBundles";

/** The page's refresh (re-read /laser/current-packaging after a click). */
export const SurplusRefreshContext = createContext(() => {});

/** Whether bundles go in crates (the API's surplus_crates_on / crates_on). Off = the plain rack, as before. */
export const SurplusCratesContext = createContext(false);

const TONE = { ok: "#7ee08a", wait: "#ffd969", bad: "#ff8a8a" };

const btn = (bg, color = "#111") => ({
  background: bg, color, border: "none", borderRadius: 8, fontWeight: 800,
  fontSize: "1.05rem", padding: "0.45rem 0.9rem", cursor: "pointer", whiteSpace: "nowrap",
});

/** "Labeled + racked" and "Reprint label" for one bundle; the outcome is said under the buttons. With crates on (or
 *  a crate already given), Mike 2026-11-04: "✓ Done: in crate BCN-031" (a tick, PIN), "Get a beacon" (no crate yet:
 *  JobQ lights a free beacon on the beacon storage rack and records the pieces in that new crate), "Use existing
 *  crate…" (the CrateBeaconPrompt box: one of the part's crates or a typed beacon) and "Flash BCN-031 again". */
export function SurplusBundleActions({ bundle, compact = false }) {
  const refresh = useContext(SurplusRefreshContext);
  const cratesOn = useContext(SurplusCratesContext);
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState(null);
  const crate = openCrate(bundle);
  const crateMode = cratesOn || !!crate;

  const pcs = bundlePieces(bundle);

  const onCrate = async (typed) => {
    let beacon = null;
    if (typed) {
      beacon = await crateBeaconPrompt(bundle);
      if (!beacon) return;
    }
    const pin = await pinPrompt(
      beacon ? `Your PIN: the ${pcs} are in the crate with beacon ${beacon}`
        : crate ? `Your PIN: the ${pcs} are in crate ${crate.beacon_id}`
          : `Your PIN: the ${pcs} are in an EMPTY crate. JobQ lights a free beacon on the beacon storage rack for it`,
      { autofill: false },
    );
    if (!pin) return;
    setBusy("crate");
    setNote(null);
    try {
      let out;
      try {
        out = await putInCrate(bundle.bundle_id, pin, { beacon });
      } catch (e) {
        if (!(e instanceof ConflictError) || e.code !== "second_crate") throw e;
        // the part already has a crate: only a second one when the operator says so
        if (!window.confirm(`${e.message}\n\nStart a SECOND crate for ${bundle.product_id || "this part"} with ${beacon}?`)) {
          setNote({ tone: "wait", text: `Put them in ${(e.detail && e.detail.crate_beacon_id) || "its crate"} instead` });
          return;
        }
        out = await putInCrate(bundle.bundle_id, pin, { beacon, secondCrate: true });
      }
      setNote({ tone: "ok", text: crateDoneText(out, { typed: !!beacon }) });
      refresh();
    } catch (e) {
      setNote({ tone: "bad", text: e.message });
    } finally {
      setBusy(null);
    }
  };

  const onFindCrate = async () => {
    if (!crate) return;
    setBusy("find");
    try {
      await findCrate(crate.crate_id);
      setNote({ tone: "ok", text: `${crate.beacon_id} is flashing${bundle.racked || !crate.new ? " on the crate" : " on the beacon storage rack"}` });
    } catch (e) {
      setNote({ tone: "bad", text: e.message });
    } finally {
      setBusy(null);
    }
  };

  const onRack = async () => {
    const pin = await pinPrompt(
      `Your PIN, to record that surplus #${bundle.bundle_id} (${bundle.qty} pcs ${bundle.product_id || ""}) `
      + "is labeled and on the surplus rack:",
      { autofill: false },
    );
    if (!pin) return;
    setBusy("rack");
    setNote(null);
    try {
      const out = await rackBundle(bundle.bundle_id, pin);
      setNote({ tone: "ok", text: out.already ? `Already racked by ${out.bundle?.racked_by_name || "someone"}` : "Recorded: on the rack" });
      refresh();
    } catch (e) {
      setNote({ tone: "bad", text: e.message });
    } finally {
      setBusy(null);
    }
  };

  const onReprint = async () => {
    setBusy("reprint");
    setNote(null);
    try {
      await reprintBundleLabel(bundle.bundle_id);
      setNote({ tone: "wait", text: "Label sent to the laser Zebra" });
      refresh();
    } catch (e) {
      setNote({ tone: "bad", text: e.message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", alignItems: compact ? "flex-end" : "flex-start" }}>
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
        {crateMode && !bundle.racked && crate && (
          <button type="button" style={btn("#7ee08a")} disabled={!!busy} onClick={() => onCrate(false)}>
            {busy === "crate" ? "Saving…" : `✓ Done: in crate ${crate.beacon_id}`}
          </button>
        )}
        {crateMode && !crate && (
          <button type="button" style={btn("#7ee08a")} disabled={!!busy} onClick={() => onCrate(false)}>
            {busy === "crate" ? "Saving…" : "Get a beacon"}
          </button>
        )}
        {crateMode && (
          <button type="button" style={btn("#ffd969")} disabled={!!busy} onClick={() => onCrate(true)}>
            {bundle.racked && crate ? "Move to another crate…" : "Use existing crate…"}
          </button>
        )}
        {crate && (
          <button type="button" style={btn("#6fb3ff")} disabled={!!busy} onClick={onFindCrate}>
            {busy === "find" ? "…" : bundle.racked ? `Find crate ${crate.beacon_id}` : `Flash ${crate.beacon_id} again`}
          </button>
        )}
        {!crateMode && (
          <button type="button" style={btn("#7ee08a")} disabled={!!busy} onClick={onRack}>
            {busy === "rack" ? "Saving…" : "✓ Labeled + racked"}
          </button>
        )}
        <button type="button" style={btn("#3a4250", "#e8ecf2")} disabled={!!busy} onClick={onReprint}>
          {busy === "reprint" ? "Sending…" : "Reprint label"}
        </button>
      </div>
      {note && <div role="status" style={{ color: TONE[note.tone], fontSize: "1rem", fontWeight: 600 }}>{note.text}</div>}
    </div>
  );
}

/**
 * A pull: pieces on the rack promised to an order's pallet. They count on that pallet only once someone moves them
 * there and clicks Moved (PIN; Mike 2026-10-26, "only when moved"). "Where?" asks JobQ which pallet takes them
 * (nothing is written) and offers Find on its beacon. A plan with no room is said before anything moves; "Move the
 * ones that fit" is a second, explicit click (the rest stay on the rack as their own pull). A pull whose allocation or
 * order was cancelled (`live` false) can't be moved: it says so (the Rack page's Undo clears it).
 */
export function SurplusPullActions({ pull, onDone, compact = false }) {
  const refresh = useContext(SurplusRefreshContext);
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState(null);
  const [where, setWhere] = useState(null);
  const [forceAsk, setForceAsk] = useState(null);

  const done = () => { refresh(); if (onDone) onDone(); };

  const onWhere = async () => {
    setBusy("where");
    setNote(null);
    try {
      const out = await previewPull(pull.pull_id);
      setWhere(out);
      if (out.no_room) setNote({ tone: "bad", text: out.no_room });
      else setNote({ tone: "ok", text: `Put them on: ${landedText(out.landed) || "no pallet"}` });
    } catch (e) {
      setNote({ tone: "bad", text: e.message });
    } finally {
      setBusy(null);
    }
  };

  const onFind = async (beaconId) => {
    setBusy("find");
    try {
      const { findBeacon } = await import("../utils/beacons");   // lazy: keeps the kiosk strip's imports light
      const out = await findBeacon(beaconId, { actor: "surplus rack" });
      if (out && out.ok === false) throw new Error(out.message || "Find failed");
      setNote({ tone: "ok", text: `${beaconId} is flashing on the pallet` });
    } catch (e) {
      setNote({ tone: "bad", text: e.message });
    } finally {
      setBusy(null);
    }
  };

  const onFindCrate = async () => {
    setBusy("crate");
    try {
      await findCrate(pull.crate_id);
      setNote({ tone: "ok", text: `${pull.crate_beacon_id} is flashing on the crate: take ${pull.qty} pcs from it` });
    } catch (e) {
      setNote({ tone: "bad", text: e.message });
    } finally {
      setBusy(null);
    }
  };

  const onMoved = async (force = false) => {
    const pin = await pinPrompt(
      force
        ? `Your PIN: the ones of ${pullLine(pull)} that fit are on the pallet now (the rest stay on the rack)`
        : `Your PIN: ${pullLine(pull)} are on the pallet now`,
      { autofill: false },
    );
    if (!pin) return;
    setBusy("moved");
    setNote(null);
    try {
      const out = await movePull(pull.pull_id, pin, force);
      setForceAsk(null);
      const rest = out.rest_qty ? ` · ${out.rest_qty} still on the rack to move` : "";
      setNote({ tone: "ok", text: out.already ? "Already moved" : `Moved: ${landedText(out.landed) || "counted on no pallet"}${rest}` });
      done();
    } catch (e) {
      if (e instanceof ConflictError && !force) setForceAsk(e.message);
      setNote({ tone: "bad", text: e.message });
    } finally {
      setBusy(null);
    }
  };

  const beacons = ((where && where.landed) || []).filter((x) => x.beacon_id);
  if (pull.live === false) {
    return (
      <div role="status" style={{ color: TONE.bad, fontSize: "1rem", fontWeight: 700 }}>
        No longer needed (its allocation or order was cancelled): leave it on the rack, Undo it on the Surplus Rack page
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", alignItems: compact ? "flex-end" : "flex-start" }}>
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
        <button type="button" style={btn("#3a4250", "#e8ecf2")} disabled={!!busy} onClick={onWhere}>
          {busy === "where" ? "Asking…" : "Where?"}
        </button>
        {pull.crate_beacon_id && pull.crate_id && (
          <button type="button" style={btn("#ffd969")} disabled={!!busy} onClick={onFindCrate}>
            {busy === "crate" ? "…" : `Find crate ${pull.crate_beacon_id}`}
          </button>
        )}
        {beacons.map((x) => (
          <button key={x.beacon_id} type="button" style={btn("#6fb3ff")} disabled={!!busy} onClick={() => onFind(x.beacon_id)}>
            {busy === "find" ? "…" : `Find ${x.short_id || x.beacon_id}`}
          </button>
        ))}
        <button type="button" style={btn("#7ee08a")} disabled={!!busy} onClick={() => onMoved(false)}>
          {busy === "moved" ? "Saving…" : "✓ Moved to the pallet"}
        </button>
        {forceAsk && (
          <button type="button" style={btn("#ff8a8a")} disabled={!!busy} onClick={() => onMoved(true)}>
            Move the ones that fit
          </button>
        )}
      </div>
      {note && <div role="status" style={{ color: TONE[note.tone], fontSize: "1rem", fontWeight: 600 }}>{note.text}</div>}
    </div>
  );
}

/** What to do with a bundle's pieces when crates are in use, as numbered steps (`crateSteps`); nothing otherwise.
 *  `dark` = the laser TV; the Surplus Rack page passes false. */
export function CrateSteps({ bundle, dark = true, compact = false }) {
  const cratesOn = useContext(SurplusCratesContext);
  const s = crateSteps(bundle, cratesOn);
  if (!s) return null;
  const color = dark ? TONE[s.tone] : ({ ok: "#2e7d32", wait: "#8a6d00", bad: "#c62828" })[s.tone];
  const size = dark ? (compact ? "1.05rem" : "1.15rem") : "0.95rem";
  if (s.done) {
    return <div data-testid="crate-hint" style={{ color, fontWeight: 800, fontSize: size }}>{s.done}</div>;
  }
  return (
    <ol data-testid="crate-hint" data-case={s.kind}
      style={{ color, fontWeight: 700, fontSize: size, margin: "0.2rem 0 0.4rem", paddingLeft: "1.6rem", lineHeight: 1.35 }}>
      {s.steps.map((t) => <li key={t}>{t}</li>)}
    </ol>
  );
}

/** The old name (one copy: the same steps). */
export const CrateHint = CrateSteps;

/** Where the SURPLUS card sends its pieces: the rack, or a crate while crates are on. */
export function SurplusWhere({ prefix = "" }) {
  const cratesOn = useContext(SurplusCratesContext);
  return <>{prefix}{cratesOn ? "a SURPLUS CRATE" : "the SURPLUS RACK"}</>;
}

function LabelStatus({ bundle }) {
  const s = labelStatusText(bundle.label_status);
  return <span style={{ color: TONE[s.tone], fontWeight: 700 }}>{s.text}</span>;
}

/** The surplus card's body (DestinationCard): what to do with these pieces, and the bundle's state. */
export function SurplusCardBody({ bundles }) {
  const { scanOn } = useContext(KioskScanContext);
  const cratesOn = useContext(SurplusCratesContext);
  const list = bundles || [];
  if (!list.length) {
    return (
      <div style={{ fontSize: "1.15rem", color: "#e8d28e", lineHeight: 1.45 }}>
        A <b>SURPLUS label</b> prints at the laser Zebra when this job is <b>Completed</b>.
        <br />Then: {cratesOn
          ? <>the steps show here. A beacon <b>flashes</b> for this part's <b>CRATE</b> (a new one on the <b>beacon storage rack</b>): the pieces go in it with the label, then {scanOn ? <><b>scan the crate's beacon</b> or press</> : "press"} <b>Done</b>.</>
          : scanOn
            ? <><b>scan it</b> → stick it on the bundle → put it on the <b>SURPLUS RACK</b>.</>
            : <>stick it on the bundle → put it on the <b>SURPLUS RACK</b> → click <b>Labeled + racked</b>.</>}
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      {list.map((b) => (
        <div key={b.bundle_id} style={{ borderTop: "1px solid #6b5a22", paddingTop: "0.5rem" }}>
          <div style={{ fontSize: "1.15rem", color: "#ffeeb0", fontWeight: 700 }}>
            Surplus #{b.bundle_id} · {b.qty} pcs
          </div>
          {b.racked ? (
            <div style={{ fontSize: "1.1rem", color: TONE.ok, fontWeight: 700 }}>
              ✓ {openCrate(b) ? `In crate ${openCrate(b).beacon_id}` : "On the rack"}{b.racked_by_name ? ` · ${b.racked_by_name}` : ""}
            </div>
          ) : (
            <>
              <div style={{ fontSize: "1.05rem", color: "#e8d28e", margin: "0.15rem 0 0.4rem" }}>
                <LabelStatus bundle={b} />{cratesOn || openCrate(b)
                  ? ""
                  : ` · ${scanOn ? "scan it → " : ""}stick it on → SURPLUS RACK`}
              </div>
              <CrateSteps bundle={b} />
              <SurplusBundleActions bundle={b} />
            </>
          )}
        </div>
      ))}
    </div>
  );
}

/** The to-do strip: every bundle not racked yet (oldest first), then every pull waiting to be MOVED from the rack
 *  to a pallet. Nothing when there is neither. */
export function SurplusRackStrip({ unracked, toMove, maxRows = 3 }) {
  const { scanOn } = useContext(KioskScanContext);
  const cratesOn = useContext(SurplusCratesContext);
  const bundles = (unracked && unracked.bundles) || [];
  const pulls = (toMove && toMove.pulls) || [];
  if (!bundles.length && !pulls.length) return null;
  const total = Math.max((unracked && unracked.total) || 0, bundles.length);
  const shown = bundles.slice(0, maxRows);
  const moveTotal = Math.max((toMove && toMove.total) || 0, pulls.length);
  const movesShown = pulls.slice(0, maxRows);
  return (
    <div
      data-testid="surplus-rack-strip"
      style={{
        background: "#3a3320", border: "3px solid #ffd969", borderRadius: 10, color: "#ffeeb0",
        padding: "0.6rem 0.9rem", flexShrink: 0,
      }}
    >
      {bundles.length > 0 && (
        <div style={{ fontSize: "1.35rem", fontWeight: 800, marginBottom: "0.35rem" }}>
          {cratesOn
            ? <>⚠ SURPLUS TO CRATE ({total}): follow the steps, then {scanOn ? "scan the crate's beacon or " : ""}press Done</>
            : <>⚠ SURPLUS TO RACK ({total}): {scanOn
              ? "scan the label, stick it on, put it on the surplus rack"
              : "label it, put it on the surplus rack, click Labeled + racked"}</>}
        </div>
      )}
      {shown.map((b) => (
        <div key={b.bundle_id} style={{ display: "flex", alignItems: "center", gap: "0.4rem 1rem", padding: "0.3rem 0", borderTop: "1px solid #6b5a22", flexWrap: "wrap" }}>
          <div style={{ fontSize: "1.2rem", fontWeight: 800, fontFamily: "monospace", minWidth: "7rem" }}>
            {b.kind === "owed" ? "OWED " : ""}#{b.bundle_id}
          </div>
          {/* the steps need the width: on a narrow screen the buttons go under them */}
          <div style={{ flex: "1 1 34rem", minWidth: 0 }}>
            <div style={{ fontSize: "1.2rem", fontWeight: 700 }}>
              {bundleLine(b)}{b.kind === "owed" ? ` · for ${b.owed_order_number || "its order"}` : ""}
            </div>
            <div style={{ fontSize: "1rem" }}><LabelStatus bundle={b} /></div>
            <CrateSteps bundle={b} compact />
          </div>
          <SurplusBundleActions bundle={b} compact />
        </div>
      ))}
      {total > shown.length && (
        <div style={{ fontSize: "1rem", color: "#d9cc8a", marginTop: "0.2rem" }}>
          + {total - shown.length} more not racked yet
        </div>
      )}
      {pulls.length > 0 && (
        <div data-testid="surplus-move-strip" style={{ fontSize: "1.35rem", fontWeight: 800, margin: bundles.length ? "0.6rem 0 0.35rem" : "0 0 0.35rem" }}>
          ➜ MOVE FROM THE RACK ({moveTotal}): they count on the pallet only once moved
        </div>
      )}
      {movesShown.map((p) => (
        <div key={p.pull_id} style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "0.3rem 0", borderTop: "1px solid #6b5a22" }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: "1.2rem", fontWeight: 700 }}>{pullLine(p)}</div>
          <SurplusPullActions pull={p} compact />
        </div>
      ))}
      {moveTotal > movesShown.length && (
        <div style={{ fontSize: "1rem", color: "#d9cc8a", marginTop: "0.2rem" }}>
          + {moveTotal - movesShown.length} more to move: the Surplus Rack page lists them all
        </div>
      )}
    </div>
  );
}
